import type { PresentationBlock } from 'paintscout'
import type { CSSProperties } from 'react'

// `background.overlay` (presentation.d.ts) is optional and, until a UI control writes it, always
// undefined on real blocks. `rgba(0,0,0,undefined)` is invalid, and since the caller previously
// built this as the `background` shorthand, one invalid value killed the entire background --
// image included (#23100). Defaulting to 0 keeps "no overlay set" behaving as "no dimming", and
// building longhand properties instead of the shorthand keeps backgroundColor from being reset.
export function getBlockBackgroundStyles(args: {
  block: PresentationBlock
  isEmbeddedQuote?: boolean
  isInvoiceTerms?: boolean
}): CSSProperties {
  const { block, isEmbeddedQuote, isInvoiceTerms } = args
  const background = block?.background ?? {}
  const sectionStyles: CSSProperties = {}

  // A poster-layout banner's image is always a forced full-bleed cover (getBlockPadding.ts) --
  // its generic `background` field isn't cleared on a layout/type change, so a color or image left
  // over from collage mode (or a converted block) must never be painted behind it (#23883).
  const isPosterBanner = block?.type === 'banner' && (block?.fields?.layout ?? 'poster') === 'poster'

  if (background.color && !isEmbeddedQuote && !isInvoiceTerms && !isPosterBanner) {
    sectionStyles.backgroundColor = background.color
  }
  if (background.textColor) {
    sectionStyles.color = background.textColor
  }

  const backgroundImage = !isPosterBanner ? background.image : undefined
  if (backgroundImage) {
    const overlay = background.overlay ?? 0
    sectionStyles.backgroundImage = `linear-gradient(rgba(0,0,0,${overlay}), rgba(0,0,0,${overlay})), url(${backgroundImage.src})`
    sectionStyles.backgroundSize = 'cover'
    sectionStyles.backgroundPosition = 'center'
    sectionStyles.backgroundRepeat = 'no-repeat'
  }

  return sectionStyles
}
